import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  Users, Plus, Edit, Trash2, DollarSign, Store, Percent,
  Calculator, ChevronLeft, ChevronRight, PieChart, User,
  Phone, Mail, FileText, Check, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPartners() {
  const { getAuthHeaders } = useAuth();
  const [partners, setPartners] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [profitData, setProfitData] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [calculatingProfit, setCalculatingProfit] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    branch_assignments: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculateProfitSharing();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [partnersRes, branchesRes] = await Promise.all([
        axios.get(`${API}/partners`, getAuthHeaders()),
        axios.get(`${API}/branches`, getAuthHeaders())
      ]);
      setPartners(partnersRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const calculateProfitSharing = async () => {
    setCalculatingProfit(true);
    try {
      const month = format(selectedMonth, 'yyyy-MM');
      const response = await axios.get(
        `${API}/partners/profit-sharing/calculate?month=${month}`,
        getAuthHeaders()
      );
      setProfitData(response.data);
    } catch (error) {
      // error handled by toast
    } finally {
      setCalculatingProfit(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingPartner(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      notes: '',
      branch_assignments: []
    });
    setShowDialog(true);
  };

  const handleOpenEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      email: partner.email || '',
      phone: partner.phone || '',
      notes: partner.notes || '',
      branch_assignments: partner.branch_assignments || []
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editingPartner) {
        await axios.put(
          `${API}/partners/${editingPartner.id}`,
          formData,
          getAuthHeaders()
        );
        toast.success('Socio actualizado');
      } else {
        await axios.post(`${API}/partners`, formData, getAuthHeaders());
        toast.success('Socio creado');
      }
      setShowDialog(false);
      fetchData();
      calculateProfitSharing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm('¿Eliminar este socio?')) return;
    
    try {
      await axios.delete(`${API}/partners/${partnerId}`, getAuthHeaders());
      toast.success('Socio eliminado');
      fetchData();
      calculateProfitSharing();
    } catch (error) {
      toast.error('Error al eliminar socio');
    }
  };

  const addBranchAssignment = () => {
    if (branches.length === 0) return;
    
    // Find a branch not already assigned
    const assignedIds = formData.branch_assignments.map(ba => ba.branch_id);
    const availableBranch = branches.find(b => !assignedIds.includes(b.id));
    
    if (!availableBranch) {
      toast.error('Todas las sucursales ya están asignadas');
      return;
    }
    
    setFormData({
      ...formData,
      branch_assignments: [
        ...formData.branch_assignments,
        { branch_id: availableBranch.id, branch_name: availableBranch.name, percentage: 0 }
      ]
    });
  };

  const updateBranchAssignment = (index, field, value) => {
    const updated = [...formData.branch_assignments];
    if (field === 'branch_id') {
      const branch = branches.find(b => b.id === value);
      updated[index] = { ...updated[index], branch_id: value, branch_name: branch?.name || '' };
    } else {
      updated[index] = { ...updated[index], [field]: field === 'percentage' ? parseFloat(value) || 0 : value };
    }
    setFormData({ ...formData, branch_assignments: updated });
  };

  const removeBranchAssignment = (index) => {
    const updated = formData.branch_assignments.filter((_, i) => i !== index);
    setFormData({ ...formData, branch_assignments: updated });
  };

  const goToPreviousMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 1));
  };

  const isCurrentMonth = format(selectedMonth, 'yyyy-MM') === format(new Date(), 'yyyy-MM');

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-partners">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight flex items-center gap-3">
              <PieChart className="w-8 h-8 text-green-500" />
              Reparto de Utilidades
            </h1>
            <p className="text-zinc-500 mt-1">Gestiona socios y porcentajes de participación</p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-green-500 text-white hover:bg-green-600"
            data-testid="add-partner-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Socio
          </Button>
        </div>

        <Tabs defaultValue="partners" className="space-y-6">
          <TabsList className="bg-zinc-800 border border-zinc-700">
            <TabsTrigger value="partners" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Socios
            </TabsTrigger>
            <TabsTrigger value="calculation" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Calculator className="w-4 h-4 mr-2" />
              Cálculo de Utilidades
            </TabsTrigger>
          </TabsList>

          {/* Partners Tab */}
          <TabsContent value="partners">
            {loading ? (
              <div className="text-center py-12 text-zinc-500">Cargando...</div>
            ) : partners.length === 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Users className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">No hay socios registrados</p>
                <Button onClick={handleOpenCreate} className="bg-green-500 text-white hover:bg-green-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Primer Socio
                </Button>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {partners.filter(p => p.active !== false).map((partner) => (
                  <Card key={partner.id} className="bg-zinc-900/50 border-zinc-800 p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                          <User className="w-6 h-6 text-green-500" />
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{partner.name}</h3>
                          {partner.email && (
                            <p className="text-sm text-zinc-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {partner.email}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenEdit(partner)}
                          className="h-8 w-8 text-zinc-400 hover:text-white"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(partner.id)}
                          className="h-8 w-8 text-zinc-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {partner.phone && (
                      <p className="text-sm text-zinc-400 flex items-center gap-2 mb-3">
                        <Phone className="w-4 h-4" />
                        {partner.phone}
                      </p>
                    )}

                    {/* Branch Assignments */}
                    <div className="space-y-2">
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Participación por Sucursal</p>
                      {partner.branch_assignments?.length > 0 ? (
                        <div className="space-y-2">
                          {partner.branch_assignments.map((ba) => (
                            <div 
                              key={ba.branch_id || ba.branch_name} 
                              className="flex items-center justify-between bg-zinc-800/50 rounded px-3 py-2"
                            >
                              <div className="flex items-center gap-2">
                                <Store className="w-4 h-4 text-zinc-500" />
                                <span className="text-sm text-zinc-300">{ba.branch_name}</span>
                              </div>
                              <span className="text-green-500 font-semibold">
                                {ba.percentage}%
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-600 italic">Sin sucursales asignadas</p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Calculation Tab */}
          <TabsContent value="calculation">
            {/* Month Selector */}
            <Card className="bg-zinc-900/50 border-zinc-800 p-4 mb-6">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToPreviousMonth}
                  className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div className="text-center">
                  <p className="text-2xl font-heading text-white capitalize">
                    {format(selectedMonth, 'MMMM yyyy', { locale: es })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToNextMonth}
                  disabled={isCurrentMonth}
                  className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </Card>

            {calculatingProfit ? (
              <div className="text-center py-12 text-zinc-500">Calculando utilidades...</div>
            ) : profitData ? (
              <div className="space-y-6">
                {/* Branch Balances */}
                <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <Store className="w-4 h-4" />
                    Balance Neto por Sucursal
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-zinc-800">
                          <th className="text-left text-xs text-zinc-500 uppercase pb-3">Sucursal</th>
                          <th className="text-right text-xs text-zinc-500 uppercase pb-3">Ventas</th>
                          <th className="text-right text-xs text-zinc-500 uppercase pb-3">Comisiones</th>
                          <th className="text-right text-xs text-zinc-500 uppercase pb-3">Gastos</th>
                          <th className="text-right text-xs text-zinc-500 uppercase pb-3">Balance Neto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(profitData.branch_balances).map(([branchId, data]) => (
                          <tr key={branchId} className="border-b border-zinc-800/50">
                            <td className="py-3 text-white">{data.branch_name}</td>
                            <td className="py-3 text-right text-green-500">${data.total_sales?.toFixed(2)}</td>
                            <td className="py-3 text-right text-red-400">-${data.total_commissions?.toFixed(2)}</td>
                            <td className="py-3 text-right text-red-400">-${data.fixed_expenses?.toFixed(2)}</td>
                            <td className="py-3 text-right font-semibold text-white">
                              ${data.net_balance?.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Profit Shares */}
                <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Reparto de Utilidades por Socio
                  </h3>
                  {profitData.profit_shares?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-zinc-800">
                            <th className="text-left text-xs text-zinc-500 uppercase pb-3">Socio</th>
                            <th className="text-left text-xs text-zinc-500 uppercase pb-3">Sucursal</th>
                            <th className="text-right text-xs text-zinc-500 uppercase pb-3">%</th>
                            <th className="text-right text-xs text-zinc-500 uppercase pb-3">Balance Neto</th>
                            <th className="text-right text-xs text-zinc-500 uppercase pb-3">Utilidad</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profitData.profit_shares.map((ps) => (
                            <tr key={`${ps.partner_name}-${ps.branch_name}`} className="border-b border-zinc-800/50">
                              <td className="py-3 text-white">{ps.partner_name}</td>
                              <td className="py-3 text-zinc-400">{ps.branch_name}</td>
                              <td className="py-3 text-right text-zinc-400">{ps.percentage}%</td>
                              <td className="py-3 text-right text-zinc-400">${ps.net_balance?.toFixed(2)}</td>
                              <td className="py-3 text-right font-semibold text-green-500">
                                ${ps.profit_share?.toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-zinc-500 text-center py-4">No hay utilidades calculadas</p>
                  )}
                </Card>

                {/* Partner Totals */}
                {profitData.partner_totals?.length > 0 && (
                  <Card className="bg-green-500/10 border-green-500/30 p-6">
                    <h3 className="text-sm text-green-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Total por Socio
                    </h3>
                    <div className="space-y-3">
                      {profitData.partner_totals.map((pt, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-zinc-900/50 rounded-lg px-4 py-3">
                          <span className="text-white font-medium">{pt.partner_name}</span>
                          <span className="text-2xl font-heading text-green-500">
                            ${pt.total_profit_share?.toFixed(2)}
                          </span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between border-t border-green-500/30 pt-4 mt-4">
                        <span className="text-green-400 font-medium">TOTAL REPARTO</span>
                        <span className="text-3xl font-heading text-green-400">
                          ${profitData.grand_total?.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Calculator className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay datos para calcular</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-green-500" />
                {editingPartner ? 'Editar Socio' : 'Nuevo Socio'}
              </DialogTitle>
            </DialogHeader>

            <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
              <div className="space-y-4 pb-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Nombre *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre del socio"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="email@ejemplo.com"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-400">Teléfono</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="55 1234 5678"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-400">Notas</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Notas adicionales"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>

                {/* Branch Assignments */}
                <div className="space-y-3 pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <Label className="text-zinc-400 flex items-center gap-2">
                      <Store className="w-4 h-4" />
                      Asignación de Sucursales
                    </Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={addBranchAssignment}
                      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Agregar
                    </Button>
                  </div>

                  {formData.branch_assignments.length === 0 ? (
                    <p className="text-sm text-zinc-600 italic py-2">Sin sucursales asignadas</p>
                  ) : (
                    <div className="space-y-3">
                      {formData.branch_assignments.map((ba, idx) => (
                        <div key={idx} className="flex items-center gap-3 bg-zinc-800/50 rounded-lg p-3">
                          <Select
                            value={ba.branch_id}
                            onValueChange={(value) => updateBranchAssignment(idx, 'branch_id', value)}
                          >
                            <SelectTrigger className="flex-1 bg-zinc-800 border-zinc-700 text-white">
                              <SelectValue placeholder="Seleccionar sucursal" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {branches.map((branch) => (
                                <SelectItem key={branch.id} value={branch.id} className="text-white">
                                  {branch.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          <div className="flex items-center gap-2 w-28">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={ba.percentage}
                              onChange={(e) => updateBranchAssignment(idx, 'percentage', e.target.value)}
                              className="bg-zinc-800 border-zinc-700 text-white text-center w-20"
                            />
                            <Percent className="w-4 h-4 text-zinc-500" />
                          </div>
                          
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            onClick={() => removeBranchAssignment(idx)}
                            className="h-8 w-8 text-zinc-400 hover:text-red-500"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowDialog(false)}
                    className="flex-1 border-zinc-700 text-zinc-300"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleSave}
                    className="flex-1 bg-green-500 text-white hover:bg-green-600"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Guardar
                  </Button>
                </div>
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
